import React, { useMemo } from 'react';
import './JobListItemWrapper.less';
import { JobType } from '../../../redux/jobs/jobs.types';
import { resolveMeterColorTailwindClass, resolveMeterType } from '../../../helper/MeterHelper';
import { useTranslation } from 'react-i18next';
import EnergyIcon from '../../../components/envago/EnergyIcon/EnergyIcon';
import JobListItem, { JobListItemIconType } from '../../../components/envago/JobListItem/JobListItem';
import { useSelector } from 'react-redux';
import { applicationSelectors } from '../../../redux/application';
import { resolveImplausibleJobReading } from '../../../helper/PlausibilityHelper';
import { EnvagoAria } from '../../../aria.types';
import { FieldVisibility } from '../../../fieldVisibility/fieldVisibility';
import { jobRowCandidates } from '../../../fieldVisibility/jobRowCandidates';

interface IJobListItemWrapperProps {
    job: JobType;
    onClick: () => void;
    isActive?: boolean;
    isGhost?: boolean;
    isDone?: boolean;
    iconRight?: JobListItemIconType;
    aria?: EnvagoAria;
    // Injected from the parent list so 8000 items don't each build their own
    // factory selector (previously two per item, invalidated on every render).
    // Deliberately required, not defaulted: a default would silently drop the
    // objectKey / locationDescription second line for a caller that forgot it.
    // JobRowList supplies all three for every list in the app.
    hasObjectKeyMultipleValues: boolean;
    hasLocationDescriptionMultipleValues: boolean;
    // Auch hier ein Prop und kein Hook: 8000 Store-Abonnements gegen null
    // einzutauschen ist kostenlos, und die Konvention steht an dieser Datei.
    visibility: FieldVisibility;
}

const JobListItemWrapper = ({
    job,
    onClick,
    isGhost = false,
    isActive = false,
    iconRight = false,
    aria,
    hasObjectKeyMultipleValues,
    hasLocationDescriptionMultipleValues,
    visibility,
}: IJobListItemWrapperProps) => {
    const { t } = useTranslation();

    const meterType = resolveMeterType(job.meter);
    const meterColor = resolveMeterColorTailwindClass(meterType);

    const { showPlausibilityInList } = useSelector(applicationSelectors.getFeaturesConfig);

    // Only recorded (done) jobs carry a reading value to judge for plausibility.
    const plausibilityReading = useMemo(() => {
        if (!showPlausibilityInList || !job.done) return undefined;
        return resolveImplausibleJobReading(job);
    }, [showPlausibilityInList, job]);

    // Drei Plätze, eine Kandidatenliste, von oben gefüllt. Reihenfolge nach
    // Wichtigkeit, nicht nach Bildschirmposition — firstLine steht optisch über
    // dem Titel.
    const [title = '', firstLine = '', secondLine = ''] = jobRowCandidates({
        job,
        visibility,
        hasObjectKeyMultipleValues,
        hasLocationDescriptionMultipleValues,
        meterTypeLabel: t(`common.metertypes.${meterType}`),
    });

    return (
        <JobListItem
            key={job.id}
            aria={aria}
            firstLine={firstLine}
            title={title}
            secondLine={secondLine}
            onClick={onClick}
            icon={<EnergyIcon iconClassName={'h-7'} meterType={meterType} />}
            color={meterColor}
            isActive={isActive}
            isGhost={isGhost}
            iconRight={iconRight}
            plausibilityReading={plausibilityReading}
        />
    );
};

// Memoisation only pays off when props are stable — the parent list must pass a
// per-jobId cached onClick and hoist any object props out of render. See JobList.
export default React.memo(JobListItemWrapper);
